import {css, Spinner, Button, view} from '@sail/ui/deprecated';
import {ChangeEvent, useCallback, useRef} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

export const messages = defineMessages({
  submitButton: {
    id: 'conversations_ui.attachments.submitButton',
    description: 'Label for button that allows user to choose a file',
    defaultMessage: 'Add attachment',
  },
  submitButtonLoading: {
    id: 'conversations_ui.attachments.submitButtonLoading',
    description: 'Label for button that indicates an upload is in progress',
    defaultMessage: 'Uploading',
  },
  submitButtonLoadingBatch: {
    id: 'conversations_ui.attachments.submitButtonLoadingBatch',
    description: 'Label for button that indicates an upload is in progress',
    defaultMessage: 'Uploading ({current} of {total})',
  },
});

type UploadButtonProps = {
  name: string;
  isLoading: boolean;
  supportedMimeTypes: string[];
  onFilesSelected: (files: FileList) => void;
  disabled?: boolean;
};

export const AttachmentUploadButton = ({
  name,
  isLoading,
  onFilesSelected,
  supportedMimeTypes,
  disabled,
}: UploadButtonProps): JSX.Element | null => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (files && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [onFilesSelected],
  );

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <view.div
      css={{
        position: css.value('relative'),
        height: css.value('28px'),
        width: 'fill',
      }}
    >
      <view.div>
        <view.input
          id={name}
          ref={fileInputRef}
          type="file"
          multiple
          data-testid="upload-button-file-input"
          name={name}
          accept={supportedMimeTypes.join(',')}
          disabled={disabled || isLoading}
          css={{
            position: css.value('absolute'),
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: css.value('0'),
            opacity: css.value('0'),
          }}
          onChange={handleFileInputChange}
        />
        <Button
          css={{width: css.value('100%')}}
          onPress={handleButtonClick}
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner data-testid="upload-button-spinner" />
          ) : (
            <FormattedMessage {...messages.submitButton} />
          )}
        </Button>
      </view.div>
    </view.div>
  );
};
